import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft, CircleCheck, CirclePause, CircleSlash, ClipboardCheck, Clock, Hammer, Info, Pause, Play, TriangleAlert,
} from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import {
  CampaignPlanRow, PlanFinding, PlanStatus, PlanValidation, campaignPlanAction, getCampaignTemplates, listCampaignPlans,
  saveCampaignPlan, validateCampaignPlan,
} from '../services/metaAdsApi';

const PINE = '#0F3F34';
const ORANGE = '#F07824';
const MINT = '#F4F8F6';
const STATUS = { good: '#0ca30c', warning: '#fab219', critical: '#d03b3b', neutral: '#8a8984' };
const TZ = 'Europe/Copenhagen';

const PLAN_STATUS: Record<PlanStatus, { label: string; color: string; Icon: LucideIcon }> = {
  draft: { label: 'Draft, has problems', color: STATUS.critical, Icon: TriangleAlert },
  validated: { label: 'Ready to build', color: STATUS.good, Icon: CircleCheck },
  queued: { label: 'Queued', color: STATUS.warning, Icon: Clock },
  building: { label: 'Building', color: STATUS.warning, Icon: Clock },
  dry_run: { label: 'Dry run done', color: STATUS.neutral, Icon: Info },
  built: { label: 'Built, paused in Meta', color: STATUS.good, Icon: CirclePause },
  failed: { label: 'Failed', color: STATUS.critical, Icon: TriangleAlert },
  active: { label: 'Active', color: STATUS.good, Icon: CircleCheck },
  paused: { label: 'Paused', color: STATUS.neutral, Icon: CirclePause },
};

function when(value: string | null | undefined) {
  if (!value) return '–';
  return new Date(value).toLocaleString('da-DK', { timeZone: TZ, dateStyle: 'short', timeStyle: 'short' });
}

function StatusPill({ status }: { status: PlanStatus }) {
  const s = PLAN_STATUS[status] ?? { label: status, color: STATUS.neutral, Icon: CircleSlash };
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600">
      <s.Icon size={12} color={s.color} aria-hidden="true" />
      {s.label}
    </span>
  );
}

type Group = { level: 'error' | 'warning'; message: string; fields: string[] };

// The same rule often fails on every ad; show it once with the places it applies to.
function groupFindings(findings: PlanFinding[]): Group[] {
  const groups = new Map<string, Group>();
  for (const f of findings) {
    const key = `${f.level}|${f.message}`;
    const g = groups.get(key) ?? { level: f.level, message: f.message, fields: [] };
    if (f.field) g.fields.push(f.field);
    groups.set(key, g);
  }
  return Array.from(groups.values());
}

function Finding({ level, message, fields }: Group) {
  const shown = fields.slice(0, 3);
  return (
    <li className="flex items-start gap-2 text-sm text-gray-700">
      {level === 'error'
        ? <TriangleAlert size={15} color={STATUS.critical} className="mt-0.5 shrink-0" aria-label="Error" />
        : <Info size={15} color={STATUS.warning} className="mt-0.5 shrink-0" aria-label="Warning" />}
      <span className="min-w-0 break-words">
        {message}
        {shown.length > 0 && (
          <span className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
            {shown.map((f) => <code key={f} className="break-all rounded bg-gray-100 px-1 py-0.5">{f}</code>)}
            {fields.length > shown.length && <span>and {fields.length - shown.length} more</span>}
          </span>
        )}
      </span>
    </li>
  );
}

function Results({ validation }: { validation: PlanValidation }) {
  const ads = validation.preview.length;
  return (
    <div className="space-y-4">
      <p className="flex items-center gap-2 text-sm font-medium text-gray-800">
        {validation.ok
          ? <CircleCheck size={16} color={STATUS.good} aria-hidden="true" />
          : <TriangleAlert size={16} color={STATUS.critical} aria-hidden="true" />}
        {validation.ok
          ? `Ready: ${ads} ads, ${validation.daily_budget ?? 0} ${validation.currency ?? ''} per day`
          : `${validation.errors.length} problems to fix before this can be built`}
      </p>
      {validation.errors.length > 0 && (
        <ul className="space-y-2">{groupFindings(validation.errors).map((g) => <Finding key={`e${g.message}`} {...g} />)}</ul>
      )}
      {validation.warnings.length > 0 && (
        <ul className="space-y-2">{groupFindings(validation.warnings).map((g) => <Finding key={`w${g.message}`} {...g} />)}</ul>
      )}
      {ads > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Ads exactly as they will be sent</h3>
          <div className="max-h-[32rem] space-y-3 overflow-y-auto pr-1">
            {validation.preview.map((ad) => (
              <div key={ad.ad} className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-[11px] text-gray-400">{ad.ad_set} › {ad.ad} · {ad.cta}{ad.landing_url ? ` · ${ad.landing_url}` : ''}</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">{ad.headline}</p>
                {ad.description && <p className="text-xs text-gray-500">{ad.description}</p>}
                <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-gray-700">{ad.primary_text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SimplyloanMetaCampaigns() {
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState('');
  const [templateFile, setTemplateFile] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [validation, setValidation] = useState<PlanValidation | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const plansQuery = useQuery({
    queryKey: ['meta-campaign-plans'],
    queryFn: listCampaignPlans,
    refetchInterval: (q) =>
      (q.state.data?.plans ?? []).some((p) => p.status === 'queued' || p.status === 'building') ? 15_000 : false,
  });
  const templatesQuery = useQuery({ queryKey: ['meta-campaign-templates'], queryFn: getCampaignTemplates, staleTime: Infinity });

  const parsed = () => {
    try {
      const value = JSON.parse(editor);
      setParseError(null);
      return value;
    } catch (e) {
      setParseError(`Not valid JSON: ${(e as Error).message}`);
      return undefined;
    }
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['meta-campaign-plans'] });

  const validate = useMutation({
    mutationFn: validateCampaignPlan,
    onSuccess: (v) => { setValidation(v); setNotice(null); },
    onError: (e: Error) => setNotice(e.message),
  });

  const save = useMutation({
    mutationFn: async ({ plan, build }: { plan: unknown; build: boolean }) => {
      const saved = await saveCampaignPlan(plan);
      if (saved.errors) {
        setValidation({ ok: !!saved.ok, errors: saved.errors, warnings: saved.warnings ?? [], preview: saved.preview ?? [],
                        daily_budget: null, currency: null });
      }
      if (build && saved.status === 'validated' && saved.plan_id) {
        const queued = await campaignPlanAction('queue', saved.plan_id);
        return { ...saved, status: queued.status, reason: queued.reason };
      }
      return saved;
    },
    onSuccess: (r) => {
      setNotice(r.reason ?? (r.status === 'queued'
        ? 'Saved and queued. The worker builds it within 5 minutes; refresh to follow it.'
        : r.status === 'validated' ? 'Saved. Build it from the plan list when you are ready.'
          : `Saved as ${r.status}. Fix the problems listed and save again.`));
      refresh();
    },
    onError: (e: Error) => setNotice(e.message),
  });

  const act = useMutation({
    mutationFn: ({ action, plan }: { action: 'queue' | 'activate' | 'pause'; plan: CampaignPlanRow }) =>
      campaignPlanAction(action, plan.id),
    onSuccess: (r) => { setNotice(r.reason ?? `Plan ${r.status}.`); refresh(); },
    onError: (e: Error) => setNotice(e.message),
  });

  const loadTemplate = (file: string) => {
    setTemplateFile(file);
    const t = templatesQuery.data?.templates.find((x) => x.file === file);
    if (t) {
      setEditor(JSON.stringify(t.spec, null, 2));
      setValidation(null);
      setParseError(null);
    }
  };

  const loadPlan = (p: CampaignPlanRow) => {
    setEditor(JSON.stringify(p.spec, null, 2));
    setTemplateFile('');
    setValidation(null);
    setNotice(p.objects > 0
      ? `Loaded ${p.plan_key}. It already has Meta objects, so save changes under a new plan_key.`
      : `Loaded ${p.plan_key}.`);
  };

  const confirmActivate = (p: CampaignPlanRow) => {
    const budget = p.validation?.daily_budget;
    const msg = `Switch "${p.name}" on in Meta? It starts spending${budget ? ` up to ${budget} ${p.validation?.currency ?? ''} per day` : ''}.`;
    if (window.confirm(msg)) act.mutate({ action: 'activate', plan: p });
  };

  const data = plansQuery.data;
  const busy = validate.isPending || save.isPending || act.isPending;
  const plans = useMemo(() => data?.plans ?? [], [data]);

  return (
    <MainLayout>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <Link to="/simplyloan/meta" className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
              <ArrowLeft size={12} aria-hidden="true" /> Meta Ads performance
            </Link>
            <h1 className="text-2xl font-semibold" style={{ color: PINE }}>Meta campaign builder</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Plans become campaigns in Meta with everything paused. Switching one on is a separate, approved step.
            </p>
          </div>
          {data && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-600">
                <Hammer size={12} color={data.mode === 'live' ? STATUS.warning : PINE} aria-hidden="true" />
                Builder: {data.mode === 'live' ? 'live (creates in Meta)' : 'dry run (sends nothing)'}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-gray-600">
                <Play size={12} color={data.activation_allowed ? STATUS.warning : STATUS.neutral} aria-hidden="true" />
                Activation from here: {data.activation_allowed ? 'on' : 'off'}
              </span>
            </div>
          )}
        </div>

        {notice && (
          <div className="flex items-start gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700" style={{ backgroundColor: MINT }}>
            <Info size={15} color={PINE} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{notice}</span>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold" style={{ color: PINE }}>Plans</h2>
            {plansQuery.isFetching && <span className="text-xs text-gray-400">Refreshing…</span>}
          </div>
          {plansQuery.isError && (
            <p className="px-4 py-6 text-sm text-gray-600">Could not load plans: {(plansQuery.error as Error).message}</p>
          )}
          {data && plans.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-gray-500">No plans yet. Start from a template below.</p>
          )}
          {plans.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead style={{ backgroundColor: MINT }}>
                  <tr className="text-left text-xs font-medium text-gray-600">
                    <th scope="col" className="px-4 py-2.5">Plan</th>
                    <th scope="col" className="px-3 py-2.5">Status</th>
                    <th scope="col" className="px-3 py-2.5 text-right">Budget per day</th>
                    <th scope="col" className="px-3 py-2.5 text-right">Meta objects</th>
                    <th scope="col" className="px-3 py-2.5">Updated</th>
                    <th scope="col" className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {plans.map((p) => (
                    <tr key={p.id} className="align-top hover:bg-gray-50">
                      <td className="max-w-[24rem] px-4 py-2.5">
                        <p className="truncate font-medium text-gray-900" title={p.name}>{p.name}</p>
                        <p className="text-xs text-gray-400">{p.plan_key} · {p.geo.toUpperCase()}{p.campaign_id && !p.campaign_id.startsWith('{{') ? ` · campaign ${p.campaign_id}` : ''}</p>
                        {p.last_error && <p className="mt-1 text-xs text-gray-600"><TriangleAlert size={12} color={STATUS.critical} className="mr-1 inline" aria-hidden="true" />{p.last_error}</p>}
                      </td>
                      <td className="px-3 py-2.5"><StatusPill status={p.status} /></td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
                        {p.validation?.daily_budget != null ? `${p.validation.daily_budget.toLocaleString('da-DK')} ${p.validation.currency ?? ''}` : '–'}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{p.objects}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">{when(p.updated_at)}<br />{p.created_by ?? ''}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap justify-end gap-1.5">
                          <button type="button" onClick={() => loadPlan(p)} className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50">
                            Load
                          </button>
                          {['validated', 'dry_run', 'failed'].includes(p.status) && (
                            <button type="button" disabled={busy} onClick={() => act.mutate({ action: 'queue', plan: p })}
                                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-white disabled:opacity-60" style={{ backgroundColor: PINE }}>
                              <Hammer size={12} aria-hidden="true" /> {data?.mode === 'live' ? 'Build paused' : 'Dry run'}
                            </button>
                          )}
                          {p.status === 'built' && (
                            <button type="button" disabled={busy || !data?.activation_allowed} onClick={() => confirmActivate(p)}
                                    title={data?.activation_allowed ? 'Switch the campaign on in Meta' : 'Activation from the dashboard is off; switch it on in Ads Manager'}
                                    className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50" style={{ backgroundColor: ORANGE }}>
                              <Play size={12} aria-hidden="true" /> Activate
                            </button>
                          )}
                          {(p.status === 'active' || p.status === 'built') && data?.mode === 'live' && (
                            <button type="button" disabled={busy} onClick={() => act.mutate({ action: 'pause', plan: p })}
                                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                              <Pause size={12} aria-hidden="true" /> Pause
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold" style={{ color: PINE }}>Plan</h2>
              <select value={templateFile} onChange={(e) => loadTemplate(e.target.value)} aria-label="Start from a template"
                      className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700">
                <option value="">Start from a template…</option>
                {(templatesQuery.data?.templates ?? []).map((t) => (
                  <option key={t.file} value={t.file}>{String((t.spec as any).campaign?.name ?? t.file)}</option>
                ))}
              </select>
            </div>
            <textarea
              value={editor}
              onChange={(e) => setEditor(e.target.value)}
              spellCheck={false}
              aria-label="Plan JSON"
              placeholder='{"plan_key": "...", "geo": "se", ...}'
              className="mt-3 h-[30rem] w-full rounded-lg border border-gray-200 p-3 font-mono text-xs leading-relaxed text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            {parseError && <p className="mt-2 text-xs text-gray-700"><TriangleAlert size={12} color={STATUS.critical} className="mr-1 inline" aria-hidden="true" />{parseError}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" disabled={busy || !editor.trim()} onClick={() => { const p = parsed(); if (p !== undefined) validate.mutate(p); }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                <ClipboardCheck size={14} aria-hidden="true" /> Check plan
              </button>
              <button type="button" disabled={busy || !editor.trim()} onClick={() => { const p = parsed(); if (p !== undefined) save.mutate({ plan: p, build: false }); }}
                      className="rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60" style={{ backgroundColor: PINE }}>
                Save plan
              </button>
              <button type="button" disabled={busy || !editor.trim()} onClick={() => { const p = parsed(); if (p !== undefined) save.mutate({ plan: p, build: true }); }}
                      className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60" style={{ backgroundColor: ORANGE }}>
                <Hammer size={14} aria-hidden="true" /> Save and build
              </button>
            </div>
          </div>

          <div className="min-w-0 rounded-xl border border-gray-200 p-4" style={{ backgroundColor: MINT }}>
            <h2 className="mb-3 text-sm font-semibold" style={{ color: PINE }}>Check</h2>
            {validation
              ? <Results validation={validation} />
              : <p className="text-sm text-gray-500">Check a plan to see its problems and every ad with its legal text, before anything reaches Meta.</p>}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
